import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';

export default function CreateUserModal({ companyId, onClose, onUserCreated }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [loginOption, setLoginOption] = useState('email'); // 'email' | 'username' | 'both'
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('user');
  const [group, setGroup] = useState('');
  const [groups, setGroups] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [userImageFile, setUserImageFile] = useState(null);
  const [userImagePreview, setUserImagePreview] = useState(null);

  useEffect(() => {
    loadGroups();
  }, [companyId]);

  async function loadGroups() {
    if (!companyId) return;
    
    try {
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        setGroups(companyData.groups || []);
      }
    } catch (err) {
      console.error('Error loading groups:', err);
    }
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Please select a JPG, PNG, or WebP image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB.');
      return;
    }

    setUserImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setUserImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    setError('');
  }

  async function uploadUserImage(userId) {
    if (!userImageFile || !userImagePreview) return null;

    try {
      // Call Cloud Function to handle image upload using data URL
      const functions = getFunctions();
      const uploadUserImageFn = httpsCallable(functions, 'uploadUserImage');
      const result = await uploadUserImageFn({
        companyId,
        userId,
        dataUrl: userImagePreview
      });

      return result.data?.imageUrl || null;
    } catch (err) {
      console.error('Error uploading image:', err);
      // Don't fail user creation if image upload fails
      return null;
    }
  }

  async function handleCreateUser(e) {
    e.preventDefault();
    setError('');
    setCreating(true);

    try {
      // Validate based on login option
      if (loginOption === 'email' || loginOption === 'both') {
        if (!email.trim()) {
          setError('Email is required.');
          setCreating(false);
          return;
        }
      }
      if (loginOption === 'username' || loginOption === 'both') {
        if (!username.trim()) {
          setError('Username is required.');
          setCreating(false);
          return;
        }
      }
      if (!password || password.length < 6) {
        setError('Password must be at least 6 characters.');
        setCreating(false);
        return;
      }

      // Create user via Cloud Function
      const functions = getFunctions();
      const createUser = httpsCallable(functions, 'createUser');

      const createUserPayload = {
        email: email || null,
        username: username || null,
        password: password,
        displayName: displayName || email?.split('@')[0] || username || 'User',
        role: role,
        group: group || null,
        companyId: companyId,
        loginOption: loginOption // 'email' | 'username' | 'both'
      };

      const result = await createUser(createUserPayload);

      if (result.data.success) {
        const userId = result.data.userId;

        // Upload image if provided
        let imageUrl = null;
        if (userImageFile && userImagePreview) {
          imageUrl = await uploadUserImage(userId);
        }

        // Notify parent component
        if (onUserCreated) {
          onUserCreated({
            id: userId,
            email,
            username,
            displayName: displayName || email?.split('@')[0] || username || 'User',
            role,
            companyId,
            imageUrl
          });
        }

        // Close modal
        onClose();
      } else {
        throw new Error(result.data.error || 'Failed to create user');
      }
    } catch (err) {
      console.error('Error creating user:', err);

      // User-friendly error messages
      if (err.message?.includes('email-already-in-use')) {
        setError('This email is already in use.');
      } else if (err.message?.includes('username-already-in-use')) {
        setError('This username is already in use.');
      } else if (err.message?.includes('weak-password')) {
        setError('Password should be at least 6 characters.');
      } else if (err.message?.includes('invalid-email')) {
        setError('Invalid email address.');
      } else if (err.code === 'functions/not-found') {
        setError('Cloud Function not deployed. Please contact your system administrator.');
      } else {
        setError(err.message || 'Failed to create user. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="border-b p-6 flex justify-between items-center">
          <h2 className="text-xl font-bold">Create New User</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            disabled={creating}
          >
            ✕
          </button>
        </div>
        
        <form onSubmit={handleCreateUser} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded text-sm">
              {error}
            </div>
          )}
          
          {/* Login Option Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Login Method <span className="text-red-500">*</span></label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="loginOption"
                  value="email"
                  checked={loginOption === 'email'}
                  onChange={(e) => setLoginOption(e.target.value)}
                  disabled={creating}
                  className="mr-2"
                />
                <span className="text-sm">Email Only</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="loginOption"
                  value="username"
                  checked={loginOption === 'username'}
                  onChange={(e) => setLoginOption(e.target.value)}
                  disabled={creating}
                  className="mr-2"
                />
                <span className="text-sm">Username Only</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="loginOption"
                  value="both"
                  checked={loginOption === 'both'}
                  onChange={(e) => setLoginOption(e.target.value)}
                  disabled={creating}
                  className="mr-2"
                />
                <span className="text-sm">Email & Username</span>
              </label>
            </div>
          </div>

          {/* Email Field */}
          {(loginOption === 'email' || loginOption === 'both') && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                placeholder="user@example.com"
                required={loginOption === 'email' || loginOption === 'both'}
                disabled={creating}
              />
            </div>
          )}

          {/* Username Field */}
          {(loginOption === 'username' || loginOption === 'both') && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Username <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s+/g, ''))}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                placeholder="johndoe"
                required={loginOption === 'username' || loginOption === 'both'}
                disabled={creating}
              />
              <p className="text-xs text-gray-500 mt-1">Letters, numbers, underscores only</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-1">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
              placeholder="Min. 6 characters"
              minLength={6}
              required
              disabled={creating}
            />
            <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
              placeholder="John Doe"
              disabled={creating}
            />
            <p className="text-xs text-gray-500 mt-1">Optional - how they appear in the app</p>
          </div>

          {/* User Image Upload */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Profile Image (Optional)
            </label>
            <div className="flex gap-3">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                disabled={creating}
                className="flex-1 text-sm"
              />
              {userImageFile && (
                <button
                  type="button"
                  onClick={() => {
                    setUserImageFile(null);
                    setUserImagePreview(null);
                  }}
                  className="px-3 py-2 text-sm bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-800"
                >
                  Remove
                </button>
              )}
            </div>
            {userImagePreview && (
              <div className="mt-3">
                <img src={userImagePreview} alt="Preview" className="h-20 w-20 rounded object-cover" />
                <p className="text-xs text-gray-500 mt-1">JPG, PNG, or WebP (max 5MB)</p>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
              disabled={creating}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          {groups.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Group/Site
              </label>
              <select
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                disabled={creating}
              >
                <option value="">None</option>
                {groups.map((g, idx) => (
                  <option key={idx} value={g}>{g}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Optional - assign user to a specific group</p>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
